/* eslint-disable no-console, no-process-env */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import HeaderComponent from './header-component';
import { withManifest } from '@godaddy/gasket-hcs';
import Cart from '@px/cart-icon';

// css for a11y skip to main component
import '@godaddy/gasket-hcs/dist/styles.css';

import '@ux/account-delegation/styles';
import '@ux/browser-deprecation-banner/styles';
import './css/index.css';

// eslint-disable-next-line max-statements
export default function Header(props) {
  const [accountTrayNav, setAccountTrayNav] = useState(props.accountTrayNav);
  const [replaceAccountTrayNavLinks, setReplaceAccountTrayNavLinks] = useState(false);
  const [cartItems, setCartItems] = useState(0);
  const [CartComponent, setCartComponent] = useState(typeof Cart === 'function' ? () => Cart : Cart);
  const [cartUrl, setCartUrl] = useState(props.urls?.checkout?.href);
  const [topNavigation, setTopNavigation] = useState(props.navigationTop);
  const [helpUrl, setHelpUrl] = useState(null);
  const [customWaffleLinks, setCustomWaffleLinks] = useState(null);

  useEffect(() => {
    if (props?.urls?.checkout?.href) {
      setCartUrl(props.urls.checkout.href);
    }
  }, [props.urls?.checkout?.href]);

  /**
   * Helper to update AccountTrayNav.
   *
   * @param {Object} props consists of updated account tray links, and a replaceAccountTrayNavLinks variable.
   * @param {Function} done Continuation to call when complete.
   * @returns {state} state change
   * @api public
   */
  const updateAccountTrayNav = useCallback(({
    newNav = null,
    replaceAccountTrayNavLinks: shouldReplaceNavLinks = false },
  done = () => {}) => {

    // If new nav is false/null that is handled in account tray.
    if (newNav && !newNav[0].caption) {
      newNav = null;
      if (process.env.NODE_NEV !== 'production') {
        console.warn(
          'The provided customAccountTrayNav of "' + newNav +
          '" is not correct data (not iterable/missing a caption). Please see the Application-header-legacy\'s readme.' +
          'The default account tray nav links will be used.'
        );
      }
    }

    setAccountTrayNav(newNav);
    setReplaceAccountTrayNavLinks(shouldReplaceNavLinks);

    done();
  }, [setAccountTrayNav, setReplaceAccountTrayNavLinks]);

  /**
   * Helper to update cart items.
   *
   * @param {number} cartItems Updated cart count.
   * @param {function} done Optional completion callback.
   * @public
   */
  const updateCart = useCallback((items, done = () => { }) => {
    const newCartItems = parseInt(items, 10) || 0;
    setCartItems(newCartItems);
    done();
  }, [setCartItems]);

  /**
   * Helper to update cart component.
   *
   * @param {React.ReactElement} component Updated React component.
   * @param {Function} done Continuation to call when complete.
   * @returns {state} state change
   * @api public
   */
  const updateCartComponent = useCallback((component, done = () => { }) => {
    setCartComponent(typeof component === 'function' ? () => component : component);
    done();
  }, [setCartComponent]);

  /**
   * Helper to update cart url
   *
   * @param {string} url Updated cart url
   * @param {function} done Optional completion callback
   * @public
   */
  const updateCartUrl = useCallback((url, done = () => { }) => {
    setCartUrl(url);
    done();
  }, [setCartUrl]);


  const updateTopNavigation = useCallback((nav, done = () => {}) => {
    if (Array.isArray(nav) && nav.length === 1) {
      setTopNavigation(nav[0]);
    }
    done();
  }, [setTopNavigation]);

  const updateHelpUrl = useCallback((url, done = () => {}) => {
    if (props.privateLabelId !== 1 && props.resellerUrls && props.resellerUrls.help) {
      done();
      return;
    }
    setHelpUrl(url);
    done();
  }, [props.resellerUrls, props.privateLabelId]);

  /**
   * Helper to update waffle menu links.
   *
   * @param {React.ReactElement} component Updated React component.
   * @param {Function} done Continuation to call when complete.
   * @returns {state} state change
   * @api public
   */
  const updateWaffleLinks = useCallback((links, done = () => {}) => {
    if (links && (Array.isArray(links.topLinks) || Array.isArray(links.quickLinks))) {
      setCustomWaffleLinks(links);
    }
    done();
  }, [setCustomWaffleLinks]);

  const headerMethods = useMemo(() => ({
    updateAccountTrayNav,
    updateCart,
    updateCartComponent,
    updateCartUrl,
    updateHelpUrl,
    updateTopNavigation,
    updateWaffleLinks
  }), [updateAccountTrayNav,
    updateCart,
    updateCartComponent,
    updateCartUrl,
    updateHelpUrl,
    updateTopNavigation,
    updateWaffleLinks
  ]);

  const HeaderRoot = useMemo(() => withManifest(HeaderComponent, {
    renderAccountDelegation: true,
    initCustomerState: true,
    initTraffic: true,
    additionalHeaderMethods: headerMethods,
    componentName: 'header'
  }), [headerMethods]);

  return (
    <HeaderRoot { ...props }
      customAccountTrayNav={ accountTrayNav }
      replaceAccountTrayNavLinks={ replaceAccountTrayNavLinks }
      cartItems={ cartItems }
      CartComponent={ CartComponent }
      cartUrl={ cartUrl }
      helpUrl={ helpUrl }
      customWaffleLinks={ customWaffleLinks }
      navigationTop={ topNavigation }
    />
  );
}

Header.displayName = 'Header';

Header.propTypes = {
  app: PropTypes.string.isRequired,
  breakpoint: PropTypes.string,
  currentHref: PropTypes.string,
  loggedIn: PropTypes.bool,
  navigationTop: PropTypes.array,
  CartComponent: PropTypes.elementType,
  features: PropTypes.object,
  preset: PropTypes.string,
  privateLabelId: PropTypes.number,
  showWaffleMenu: PropTypes.bool,
  customWaffleLinks: PropTypes.object,
  resellerUrls: PropTypes.object,
  accountTrayNav: PropTypes.array,
  urls: PropTypes.object
};
